// ============ SUPER TD · NAS100 — dashboard card ============
// Renders the Super Cycle Time & Duration seasonality system (research/nas100-seasonality/):
// live server clock (EET), current EA ON/OFF window with countdown, today's
// seasonal verdict (day/weekday/week/month), the Top-3 green/red intraday
// ticks, and the monthly green-probability strip. Data comes from
// /api/supertd (the same super_td.json the 24h EA controller reads);
// the card hides itself when the schedule file is absent.
import React, { useEffect, useMemo, useState } from 'react';
import { SecHead } from './core';
import { useT } from '../lib/i18n';
import { INDICES } from '../data';

// Monthly green % — the Super Cycle Time & Duration engine's own figures (v0.3.0, Yahoo ^NDX,
// "closed above the previous month's close"), so this strip and the per-asset
// statistics card below it cannot disagree.
//
// The previous constants were hand-entered and had drifted: July read 77.3% on
// the 21-year row and 64.3% on the full-history row, against 85.7% and 67.5%
// here. The engine's own tools/verify_nas100.py flagged it — 77.3% is 17/22 and
// 64.3% is 27/42, neither of which is reachable on the 21 and 41 observations
// those rows actually have. Jan-Jun matched exactly, which is why it went
// unnoticed. Re-run that tool after touching these.
const MONTH_PCT_21Y = [66.7, 42.9, 66.7, 71.4, 66.7, 52.4, 85.7, 61.9, 52.4, 57.1, 71.4, 52.4];
const MONTH_PCT_41Y = [70.7, 48.8, 65.9, 61.0, 68.3, 51.2, 67.5, 60.0, 55.0, 62.5, 68.3, 53.7];
const MONTH_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const MONTH_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WD_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WD_TH = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์'];

/** Wall-clock parts in the EA server timezone (EET, DST-aware). */
function serverNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Bucharest', hour12: false,
    hour: '2-digit', minute: '2-digit', second: '2-digit', day: 'numeric', month: 'numeric', weekday: 'short',
  }).formatToParts(new Date());
  const get = (k: string) => parts.find(p => p.type === k)?.value || '';
  return {
    hm: `${get('hour')}:${get('minute')}`,
    hms: `${get('hour')}:${get('minute')}:${get('second')}`,
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
    day: Number(get('day')),
    month: Number(get('month')),      // 1-12
    weekday: get('weekday'),          // 'Wed'
  };
}
const toMin = (hm: string) => { const [h, m] = hm.split(':').map(Number); return h * 60 + m; };
const fmtLeft = (min: number, t: (a: string, b: string) => string) =>
  min >= 60 ? `${Math.floor(min / 60)} ${t('ชม.', 'h')} ${min % 60} ${t('นาที', 'm')}` : `${min} ${t('นาที', 'm')}`;

export function SuperTdCard() {
  const t = useT();
  const [cfg, setCfg] = useState<any | null>(null);
  const [now, setNow] = useState(serverNow());
  const [win, setWin] = useState<'21Y' | '41Y'>('21Y');
  const [tick, setTick] = useState(0); // bumps on every live price update

  useEffect(() => {
    let alive = true;
    fetch('/api/supertd').then(r => (r.ok ? r.json() : null)).then(d => { if (alive) setCfg(d); }).catch(() => {});
    const clock = setInterval(() => setNow(serverNow()), 1000);
    // Re-read the live NAS100 quote whenever the live engine emits a tick.
    const onLive = () => setTick(x => x + 1);
    window.addEventListener('mm-live-tick', onLive);
    return () => { alive = false; clearInterval(clock); window.removeEventListener('mm-live-tick', onLive); };
  }, []);

  // Live NAS100 (^NDX) quote — the live engine mutates the INDICES row in place.
  const ndx = useMemo(() => {
    const ix = INDICES.find(x => x.sym === 'NDX');
    return ix ? { price: ix.price, chg: ix.chg } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const status = useMemo(() => {
    if (!cfg) return null;
    const m = now.minutes;
    const on = (cfg.eaWindows?.on || []).find((w: any) => m >= toMin(w.start) && m < toMin(w.end));
    if (on) return { on: true, until: toMin(on.end) - m, window: on, next: null as any };
    // next ON window today, else tomorrow's first
    const upcoming = (cfg.eaWindows?.on || []).map((w: any) => ({ w, at: toMin(w.start) })).filter((x: any) => x.at > m).sort((a: any, b: any) => a.at - b.at)[0];
    const first = (cfg.eaWindows?.on || [])[0];
    return upcoming
      ? { on: false, until: upcoming.at - m, window: upcoming.w, next: upcoming.w }
      : { on: false, until: (24 * 60 - m) + (first ? toMin(first.start) : 0), window: first, next: first };
  }, [cfg, now.minutes]);

  const verdict = useMemo(() => {
    if (!cfg) return [];
    const mName = MONTH_EN[now.month - 1];
    const wk = now.day <= 7 ? 'W1(1-7)' : now.day <= 14 ? 'W2(8-14)' : now.day <= 21 ? 'W3(15-21)' : 'W4(22-28)';
    const mark = (good: boolean, bad: boolean) => (good ? '✓' : bad ? '✗' : '–');
    const cls = (good: boolean, bad: boolean) => (good ? 'up' : bad ? 'down' : 'dim');
    const dom = cfg.dayOfMonth || {}; const wd = cfg.weekday || {}; const wom = cfg.weekOfMonth || {}; const mo = cfg.month || {};
    return [
      { k: t('วันที่', 'Day') + ` ${now.day}`, good: (dom.green || []).includes(now.day), bad: (dom.red || []).includes(now.day) },
      { k: t(WD_TH[WD_EN.indexOf(now.weekday)] || now.weekday, now.weekday), good: (wd.green || []).includes(now.weekday), bad: (wd.red || []).includes(now.weekday) },
      { k: wk.split('(')[0], good: (wom.green || []).includes(wk), bad: (wom.weak || []).includes(wk) },
      { k: t(MONTH_TH[now.month - 1], mName), good: (mo.green || []).includes(mName), bad: (mo.red || []).includes(mName) },
    ].map(v => ({ label: v.k, mark: mark(v.good, v.bad), cls: cls(v.good, v.bad) }));
  }, [cfg, now.day, now.weekday, now.month, t]);

  // Next 5 trading sessions (Mon-Fri, EET) scored against the same rules as
  // today's verdict — adapted from NOPOLY's "next sessions odds" strip.
  const nextSessions = useMemo(() => {
    if (!cfg) return [];
    const out: { label: string; score: number }[] = [];
    const dom = cfg.dayOfMonth || {}; const wd = cfg.weekday || {}; const mo = cfg.month || {}; const wom = cfg.weekOfMonth || {};
    const d = new Date();
    for (let i = 0; out.length < 5 && i < 10; i++) {
      const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Bucharest', weekday: 'short', day: 'numeric', month: 'numeric' })
        .formatToParts(new Date(d.getTime() + i * 864e5));
      const g = (k: string) => p.find(x => x.type === k)?.value || '';
      const wday = g('weekday'); const day = Number(g('day')); const mon = Number(g('month'));
      if (wday === 'Sat' || wday === 'Sun') continue;
      const wk = day <= 7 ? 'W1(1-7)' : day <= 14 ? 'W2(8-14)' : day <= 21 ? 'W3(15-21)' : 'W4(22-28)';
      let score = 0;
      if ((dom.green || []).includes(day)) score++; if ((dom.red || []).includes(day)) score--;
      if ((wd.green || []).includes(wday)) score++; if ((wd.red || []).includes(wday)) score--;
      if ((wom.green || []).includes(wk)) score++; if ((wom.weak || []).includes(wk)) score--;
      if ((mo.green || []).includes(MONTH_EN[mon - 1])) score++; if ((mo.red || []).includes(MONTH_EN[mon - 1])) score--;
      const abbr: Record<string, string> = { Mon: 'จ', Tue: 'อ', Wed: 'พ', Thu: 'พฤ', Fri: 'ศ' };
      out.push({ label: `${t(abbr[wday] || wday, wday)} ${day}`, score });
    }
    return out;
  }, [cfg, now.day, t]);

  // Nearest upcoming intraday edge tick (green or red), with a live countdown.
  // Green/red slots come straight from superTD; the earliest one after "now"
  // wins, otherwise we roll over to the first slot of tomorrow.
  const nextTick = useMemo(() => {
    if (!cfg) return null;
    const slots = [
      ...(cfg.superTD?.green || []).map((g: any) => ({ slot: g.slot, kind: 'green' as const })),
      ...(cfg.superTD?.red || []).map((r: any) => ({ slot: r.slot, kind: 'red' as const })),
    ].map(s => ({ ...s, at: toMin(s.slot) })).sort((a, b) => a.at - b.at);
    if (!slots.length) return null;
    const ahead = slots.find(s => s.at > now.minutes);
    return ahead
      ? { ...ahead, left: ahead.at - now.minutes }
      : { ...slots[0], left: (24 * 60 - now.minutes) + slots[0].at };
  }, [cfg, now.minutes]);

  if (!cfg || !status) return null; // no schedule file → no card

  const pct = win === '21Y' ? MONTH_PCT_21Y : MONTH_PCT_41Y;
  const heat = (p: number) => (p >= 65 ? 'rgba(0,224,176,0.55)' : p >= 55 ? 'rgba(0,224,176,0.25)' : p >= 50 ? 'rgba(255,255,255,0.08)' : 'rgba(255,59,92,0.35)');

  return (
    <div className="mm-card glass mm-supertd">
      <SecHead th="Super Cycle Time & Duration · NAS100" en="SUPER CYCLE TIME & DURATION · NAS100 SEASONALITY"></SecHead>

      <div className="mm-std-top">
        <div className={`mm-std-status ${status.on ? 'on' : 'off'}`}>
          <b className="mono">{status.on ? 'EA ON' : 'EA OFF'}</b>
          <span>{status.on
            ? `${t('เทรดฝั่ง', 'bias')} ${status.window?.bias === 'long' ? 'LONG' : status.window?.bias} · ${t('ปิดใน', 'closes in')} ${fmtLeft(status.until, t)}`
            : `${t('หน้าต่างถัดไป', 'next window')} ${status.next?.start} (${fmtLeft(status.until, t)})`}</span>
        </div>
        {ndx && (
          <div className={`mm-std-quote mono ${ndx.chg >= 0 ? 'up' : 'down'}`}>
            <span className="dim">NAS100</span>
            <b>{ndx.price.toLocaleString('en-US', { maximumFractionDigits: 2 })}</b>
            <em>{ndx.chg >= 0 ? '▲' : '▼'} {ndx.chg >= 0 ? '+' : ''}{ndx.chg.toFixed(2)}%</em>
          </div>
        )}
        <div className="mm-std-clock mono">
          <b>{now.hms}</b>
          <span className="dim">{t('เวลา Server (EET)', 'Server time (EET)')}</span>
        </div>
      </div>

      <div className="mm-std-verdict">
        <span className="mm-std-lbl mono">{t('วันนี้', 'TODAY')}</span>
        {verdict.map(v => <span key={v.label} className={`mm-std-chip ${v.cls}`}>{v.label} <b>{v.mark}</b></span>)}
      </div>

      <div className="mm-std-verdict">
        <span className="mm-std-lbl mono">{t('5 เซสชันถัดไป', 'NEXT 5')}</span>
        {nextSessions.map(s => (
          <span key={s.label} className={`mm-std-chip ${s.score > 0 ? 'up' : s.score < 0 ? 'down' : 'dim'}`}
            title={t('คะแนนรวม 4 มิติ (วัน/วันในสัปดาห์/สัปดาห์/เดือน)', 'Combined 4-dimension score')}>
            {s.label} <b>{s.score > 0 ? '+' + s.score : s.score}</b>
          </span>
        ))}
      </div>

      <div className="mm-std-windows">
        {(cfg.eaWindows?.on || []).map((w: any, i: number) => (
          <div className="mm-std-win" key={i} title={w.reason}>
            <span className="mono up">▶ {w.start}–{w.end}</span>
            <span className="dim">{w.reason}</span>
          </div>
        ))}
      </div>

      <div className="mm-std-ticks">
        {nextTick && (
          <span className={`mm-std-tick next ${nextTick.kind === 'green' ? 'up' : 'down'} mono`}
            title={t('จุดสัญญาณ intraday ที่ใกล้ที่สุด', 'Nearest upcoming intraday edge tick')}>
            ⏭ {nextTick.kind === 'green' ? '🟢' : '🔴'} {nextTick.slot} · {fmtLeft(nextTick.left, t)}
          </span>
        )}
        {(cfg.superTD?.green || []).map((g: any) => (
          <span key={g.slot} className="mm-std-tick up mono" title={`${g.tf} · ${t('เขียว', 'green')} ${g.green}`}>🟢 {g.slot}</span>
        ))}
        {(cfg.superTD?.red || []).map((r: any) => (
          <span key={r.slot} className="mm-std-tick down mono" title={`${r.tf} · ${t('เขียวเพียง', 'green only')} ${r.green}`}>🔴 {r.slot}</span>
        ))}
        <span className="mm-std-tick dim mono" title={t('จุดที่ควรระวังเป็นพิเศษ', 'Extra-caution ticks')}>⚠ {(cfg.cautionTicks || []).join(' · ')}</span>
      </div>

      <div className="mm-std-months">
        <span className="mm-std-lbl mono">
          {t('โอกาสปิดเขียวรายเดือน', 'MONTHLY GREEN %')}
          {/* labelled 21Y, not 20Y: the figures always were the engine's
              21-year window, the old label just said otherwise */}
          <button className={`mm-std-winbtn ${win === '21Y' ? 'on' : ''}`} onClick={() => setWin('21Y')}>21Y</button>
          <button className={`mm-std-winbtn ${win === '41Y' ? 'on' : ''}`} onClick={() => setWin('41Y')}>41Y</button>
        </span>
        <div className="mm-std-heat">
          {pct.map((p, i) => (
            <span key={i} className={`mm-std-cell ${i === now.month - 1 ? 'now' : ''}`} style={{ background: heat(p) }}
              title={`${MONTH_EN[i]} · ${p}%`}>
              <em>{t(MONTH_TH[i], MONTH_EN[i])}</em>
              <b className="mono">{Math.round(p)}</b>
            </span>
          ))}
        </div>
      </div>

      <p className="mm-footnote dim">
        {t('ข้อมูล: Yahoo ^NDX 1985-2026 + Dukascopy intraday (EET) · ตัวควบคุม EA 24 ชม. และแจ้งเตือน Discord รายวันอ่านตารางเดียวกันนี้ · สถิติอดีตไม่การันตีอนาคต',
          'Data: Yahoo ^NDX 1985-2026 + Dukascopy intraday (EET) · the 24h EA controller and daily Discord alert read this same schedule · past stats never guarantee the future')}
      </p>
    </div>
  );
}
